import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { useMemberId } from '@trello/authentication';
import { boardSwitcherSharedState } from '@trello/board-switcher';
import { useOneTimeMessagesDismissed } from '@trello/business-logic-react/member';
import { useFeatureGate } from '@trello/feature-gate-client';
import {
  SpotlightManager,
  SpotlightTarget,
  SpotlightTransition,
} from '@trello/nachos/onboarding-experimental';

import { useBoardIdFromBoardOrCardRoute } from 'app/src/components/Board/useBoardIdFromBoardOrCardRoute';
import { useIsGASpotlightTourOpen } from 'app/src/components/PersonalProductivityBeta/betaGASharedState';
import {
  ISLAND_NAV_SPOTLIGHT_MESSAGE_ID,
  usePersonalProductivitySpotlightTour,
} from 'app/src/components/PersonalProductivityOnboarding';
import { useNewUserCohortFragment } from 'app/src/components/PersonalProductivityOnboarding/NewUserCohortFragment.generated';
import { useBoardSwitcherButtonPulseAndSpotlight } from 'app/src/components/PersonalProductivityOnboarding/useBoardSwitcherButtonPulseAndSpotlight';
import { useBoardSwitcher } from './useBoardSwitcher';

export const BoardSwitcherSpotlight = ({
  children,
}: {
  children: ReactElement;
}) => {
  const memberId = useMemberId();
  const [isSpotlightActive, setIsSpotlightActive] = useState(false);
  const { value: isSpotlightEnabled, loading: isSpotlightEnabledLoading } =
    useFeatureGate('trello_personal_productivity_release');
  const { recentLoading } = useBoardSwitcher();

  const { isOneTimeMessageDismissed } = useOneTimeMessagesDismissed();

  const { data } = useNewUserCohortFragment({
    from: { id: memberId },
  });

  const isGA = data?.cohorts?.userCohortPersonalProductivity === 'ga';
  const isExistingUserSpotlightTourOpenForGA = useIsGASpotlightTourOpen();
  const tourIsActive =
    isExistingUserSpotlightTourOpenForGA ||
    (!isGA && !isOneTimeMessageDismissed(ISLAND_NAV_SPOTLIGHT_MESSAGE_ID));

  const boardId = useBoardIdFromBoardOrCardRoute();
  const { renderActiveSpotlight } = usePersonalProductivitySpotlightTour({
    // We don't need to account for the panel mode here because "modal" is
    // the default mode and the user can't switch to "panel" mode without
    // first dismissing the spotlight.
    source: 'boardSwitcherModal',
    boardId,
  });

  const {
    shouldShowSpotlight: shouldShowIndividualSpotlight,
    renderSingleBoardSwitcherSpotlight,
  } = useBoardSwitcherButtonPulseAndSpotlight();

  const onNextFrame = (func: () => void, delay = 0): number => {
    const nextFrameTimeoutId = window.setTimeout(() => {
      window.requestAnimationFrame(() => window.requestAnimationFrame(func));
    }, delay);
    return nextFrameTimeoutId;
  };

  const canOpenSpotlight =
    !isSpotlightEnabledLoading &&
    isSpotlightEnabled &&
    !recentLoading &&
    tourIsActive;

  useEffect(() => {
    if (canOpenSpotlight) {
      Analytics.sendViewedComponentEvent({
        componentType: 'spotlight',
        componentName: 'boardSwitcherSpotlight',
        source: 'boardSwitcherModal',
      });
      boardSwitcherSharedState.setValue({
        isOpen: true,
        isSpotlightOpen: true,
      });
      const nextFrameTimeoutId = onNextFrame(() => {
        setIsSpotlightActive(true);
      }, 100);
      return () => window.clearTimeout(nextFrameTimeoutId);
    }
  }, [canOpenSpotlight, setIsSpotlightActive]);

  return (
    <SpotlightManager blanketIsTinted={false}>
      <SpotlightTarget name="board-switcher-spotlight">
        {children}
      </SpotlightTarget>
      <SpotlightTransition>
        {isSpotlightActive &&
          !shouldShowIndividualSpotlight &&
          renderActiveSpotlight()}
        {shouldShowIndividualSpotlight &&
          !isSpotlightActive &&
          renderSingleBoardSwitcherSpotlight()}
      </SpotlightTransition>
    </SpotlightManager>
  );
};
